import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { difference, isEmpty, mapValues } from 'lodash';
import queryString from 'query-string';
import { CustomParamsSerializer } from 'axios';

import { FacetKey } from '@app/components';
import { useSearchContext } from '@app/contexts';
import { useFeatureFlag, useLocationsFetch } from '@app/hooks';
import { makeRequest } from '@app/utils';
import { Feature } from '@app/constants';

import { getCQLState } from '../../../utils';
import { getMappedFacetValues } from '../utils';
import { FacetInstanceDto, FacetsDto, UnmappedFacetValue } from '../constants';
import { useInitialFacets } from './useInitialFacets';

interface FacetRequestProps {
  isStatePristine: boolean;
  expandedFacets: FacetKey[];
}

const facetLimit = 1000;

export const useFacetsRequest = ({
  expandedFacets,
  isStatePristine,
}: FacetRequestProps) => {
  const { $t } = useIntl();
  const initialFacets = useInitialFacets();
  const [fetchedFacets, setFetchedFacets] = useState(initialFacets);
  const [expandedFacetsQuery, setExpandedFacetsQuery] = useState('');
  const { isActive: isConsortiaLogin } = useFeatureFlag({
    name: Feature.consortiaLoginFlow,
  });
  const locationsQuery = useLocationsFetch();
  const {
    state: { option, query, publicationYear, facets },
  } = useSearchContext();

  useEffect(() => {
    if (expandedFacets.length) {
      setExpandedFacetsQuery(
        expandedFacets.map(facet => `${facet}:${facetLimit}`).join(',')
      );
    }
  }, [expandedFacets]);

  const facetsQuery = useQuery(
    ['facets', expandedFacetsQuery, facets, option, query, publicationYear],
    () =>
      makeRequest<FacetsDto>({
        url: '/search/instances/facets',
        params: {
          facet: expandedFacetsQuery,
          ...getCQLState({
            query,
            facets,
            option,
            publicationYear,
            hasRequestedFacets: Boolean(expandedFacetsQuery),
            isConsortiaMode: isConsortiaLogin,
          }),
        },
        paramsSerializer: queryString.stringify as CustomParamsSerializer,
      }),
    {
      enabled:
        (!isStatePristine || !isEmpty(facets)) && Boolean(expandedFacetsQuery),
    }
  );

  const getNullishFacetAttributes = (
    facetKey: FacetKey,
    facetAttributes: UnmappedFacetValue[]
  ): UnmappedFacetValue[] => {
    if (!facets) {
      return [];
    }

    return difference(
      facets[facetKey],
      facetAttributes.map(
        ({ id }) =>
          facets[facetKey]?.find(facetAttribute => facetAttribute === id)
      )
    ).map((facetValue: string) => ({ id: facetValue, totalRecords: 0 }));
  };

  useEffect(() => {
    if (facetsQuery.isError) {
      setFetchedFacets(
        previousFacets =>
          mapValues(previousFacets, (value, key) => {
            const isFacetExpanded = expandedFacets?.includes(key as FacetKey);
            const isFacetNullish = value?.values === null;

            return {
              ...value,
              isError: isFacetExpanded ? isFacetNullish : false,
            };
          }) as FacetInstanceDto
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facetsQuery.isError]);

  useEffect(() => {
    if (facetsQuery.data) {
      const updatedFacets = expandedFacets.reduce(
        (newFacetsToFetch, facetKey) => {
          if (!newFacetsToFetch[facetKey]) {
            return newFacetsToFetch;
          }

          const facetAttributes =
            facetsQuery.data.facets[facetKey]?.values || [];
          const nullishFacetAttributes = getNullishFacetAttributes(
            facetKey,
            facetAttributes
          );

          newFacetsToFetch[facetKey] = {
            ...newFacetsToFetch[facetKey],
            values: getMappedFacetValues({
              $t,
              facetName: facetKey,
              values: [...nullishFacetAttributes, ...facetAttributes],
              locations: locationsQuery.data,
            }),
            isError: false,
            isSearchable: facetKey !== 'patronItemStatus',
          };

          return newFacetsToFetch;
        },
        { ...fetchedFacets }
      );

      setFetchedFacets(updatedFacets);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facetsQuery.data]);

  return fetchedFacets;
};
